import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { 
  Check, Monitor, FileCheck, BarChart3, Brain, Users,
  Play, Star, MessageCircle, Clock, Mail
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { usePublicTracking } from "@/hooks/usePublicTracking";
import gestaoDocumentosImg from "@/assets/gestao-documentos.jpg";
import admissaoProgressoImg from "@/assets/admissao-progresso.jpg";
import contratosModeloImg from "@/assets/contratos-modelo.jpg";
import lyraLogo from "@/assets/lyra-logo.png";

const demoFormSchema = z.object({
  nome: z.string().trim()
    .min(2, "Nome muito curto")
    .max(100, "Nome muito longo"),
  telefone: z.string().trim()
    .min(10, "Telefone deve ter pelo menos 10 dígitos")
    .max(20, "Telefone muito longo")
    .regex(/^\d+$/, "Apenas números"),
  empresa: z.string().trim()
    .min(2, "Nome da empresa é obrigatório")
    .max(100),
  quantidadePessoas: z.string()
    .refine(val => val !== "", { message: "Selecione uma opção" }),
  email: z.string().trim()
    .email("E-mail inválido")
    .max(255)
});

type DemoFormData = z.infer<typeof demoFormSchema>;

export default function Home() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [lastSubmit, setLastSubmit] = useState<number>(0);
  const [isScrolled, setIsScrolled] = useState(false);
  const [billingPeriod, setBillingPeriod] = useState<'mensal' | 'anual'>('mensal');
  const { trackPageView, trackClick, trackScroll, trackFormInteraction, trackConversion } = usePublicTracking();

  const form = useForm<DemoFormData>({
    resolver: zodResolver(demoFormSchema),
    mode: "onBlur",
    defaultValues: {
      nome: "",
      telefone: "",
      empresa: "",
      quantidadePessoas: "",
      email: ""
    }
  });

  const heroSlides = [
    {
      title: "Transforme seu RH com Inteligência Artificial",
      subtitle: "Automatize processos, reduza erros e aumente a eficiência com IA de última geração."
    },
    {
      title: "Admissão Digital em Minutos",
      subtitle: "Do envio de documentos à assinatura do contrato, tudo automatizado com IA de última geração."
    },
    {
      title: "Gestão Inteligente de Documentos",
      subtitle: "IA que lê, valida e organiza automaticamente todos os documentos dos candidatos."
    },
    {
      title: "Integração com WhatsApp",
      subtitle: "Comunique-se com candidatos de forma automática e acompanhe todo o processo em tempo real."
    }
  ];

  useEffect(() => {
    trackPageView();
    
    const handleClickEvent = (e: MouseEvent) => trackClick(e);
    document.addEventListener('click', handleClickEvent);
    
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 6000);
    
    return () => {
      clearInterval(timer);
      document.removeEventListener('click', handleClickEvent);
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            trackScroll(entry.target.id);
          }
        });
      },
      { threshold: 0.5 }
    );
    
    ['hero', 'features', 'pricing', 'testimonials', 'contact'].forEach(id => {
      const element = document.getElementById(id);
      if (element) observer.observe(element);
    });
    
    return () => observer.disconnect();
  }, []);

  const scrollToSection = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  const plansData = {
    essencial: {
      name: 'Start',
      monthlyPrice: 219.90,
      annualPrice: 175.90,
      contracts: 5,
      monthlyFeatures: [
        'Perfeito para quem está começando a contratar com inteligência.'
      ],
      annualFeatures: [
        'Comece o ano economizando — 2 meses grátis e suporte garantido.'
      ]
    },
    profissional: {
      name: 'Growth',
      monthlyPrice: 379.90,
      annualPrice: 304.90,
      contracts: 10,
      monthlyFeatures: [
        'Mais contratações, menos esforço — o equilíbrio ideal para equipes em expansão.'
      ],
      annualFeatures: [
        'Cresça com previsibilidade — economize e mantenha seu RH automatizado o ano inteiro.'
      ]
    },
    corporativo: {
      name: 'Scale',
      monthlyPrice: 849.90,
      annualPrice: 679.90,
      contracts: 30,
      monthlyFeatures: [
        'Gestão de alto volume com automação total — produtividade sem limites.'
      ],
      annualFeatures: [
        'Potência máxima para grandes times — economize e escale com segurança.'
      ]
    }
  };

  const handleSubscribe = (planType: string) => {
    const planId = `${planType}-${billingPeriod}`;
    navigate(`/plans/${planId}`);
  };

  const onSubmit = async (data: DemoFormData) => {
    console.log("✅ Formulário válido, enviando:", data);
    
    const now = Date.now();
    if (now - lastSubmit < 5000) {
      toast({
        title: "Aguarde",
        description: "Aguarde 5 segundos entre envios",
        variant: "destructive"
      });
      return;
    }
    setLastSubmit(now);

    try {
      // Enviar e-mail via Edge Function
      const { error } = await supabase.functions.invoke('enviar-email-demo', {
        body: {
          nome: data.nome,
          telefone: data.telefone,
          empresa: data.empresa,
          quantidadePessoas: data.quantidadePessoas,
          email: data.email
        }
      });
      
      if (error) throw error;
      
      // Toast de sucesso
      toast({
        title: "✅ Enviado com sucesso!",
        description: "Entraremos em contato em breve.",
      });
      
      // Tracking de conversão
      await trackConversion('demo_form_home');
      
      // Redirecionar para página de sucesso
      navigate('/demo/sucesso');
      
    } catch (error) {
      // Erro silencioso - não mostra toast
      console.error("Erro ao enviar:", error);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* NAVEGAÇÃO FIXA */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white shadow-md border-b border-primary/10">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 -ml-2">
              <img 
                src={lyraLogo} 
                alt="Lyra RH Logo" 
                className="h-14 w-auto"
              />
            </div>

            <div className="hidden md:flex items-center gap-6">
              <button onClick={() => scrollToSection('inicio')} 
                className="text-foreground hover:text-primary transition-colors">
                Início
              </button>
              <button onClick={() => scrollToSection('beneficios')} 
                className="text-foreground hover:text-primary transition-colors">
                Benefícios
              </button>
              <button onClick={() => scrollToSection('planos')} 
                className="text-foreground hover:text-primary transition-colors">
                Planos
              </button>
              <button onClick={() => scrollToSection('clientes')} 
                className="text-foreground hover:text-primary transition-colors">
                Clientes
              </button>
              <button onClick={() => scrollToSection('contato')} 
                className="text-foreground hover:text-primary transition-colors">
                Contato
              </button>
            </div>

            <div className="flex items-center gap-3">
              <Link to="/demo">
                <Button variant="outline" size="sm"
                  className="border-primary text-primary hover:bg-primary/10">
                  Agende uma Demo
                </Button>
              </Link>
              <Link to="/auth">
                <Button size="sm" className="bg-primary hover:bg-primary/90">
                  Login
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* SEÇÃO HERO */}
      <section id="inicio" className="pt-24 min-h-[600px] relative overflow-hidden"
        style={{ background: 'var(--gradient-hero)' }}>
        
        <div className="absolute inset-0 pointer-events-none opacity-10">
          <Monitor className="absolute top-20 left-10 w-24 h-24" />
          <FileCheck className="absolute top-40 right-20 w-20 h-20" />
          <BarChart3 className="absolute bottom-32 left-1/4 w-28 h-28" />
          <Brain className="absolute top-1/3 right-1/3 w-32 h-32" />
          <Users className="absolute bottom-20 right-10 w-24 h-24" />
        </div>

        <div className="container mx-auto px-4 pt-16 pb-8 relative z-10 min-h-[60vh]">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center h-full">
            
            <div className="text-white space-y-6 flex flex-col justify-center items-center text-center h-full">
              <div className="space-y-4">
                <h1 className="text-4xl md:text-5xl font-bold leading-tight transition-all duration-500">
                  {heroSlides[currentSlide].title}
                </h1>
                <p className="text-lg md:text-xl opacity-90">
                  {heroSlides[currentSlide].subtitle}
                </p>
              </div>

              <div className="flex gap-3 justify-center">
                <Badge variant="secondary" className="bg-white/90 text-primary px-4 py-2 text-sm">
                  <Check className="w-4 h-4 mr-2" />
                  Validação Automática com IA
                </Badge>
                <Badge variant="secondary" className="bg-white/90 text-primary px-4 py-2 text-sm">
                  <Check className="w-4 h-4 mr-2" />
                  Integração WhatsApp
                </Badge>
                <Badge variant="secondary" className="bg-white/90 text-primary px-4 py-2 text-sm">
                  <Check className="w-4 h-4 mr-2" />
                  Redução de 80% no Tempo
                </Badge>
              </div>

              <div className="flex gap-2 pt-4">
                {heroSlides.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentSlide(index)}
                    className={`w-3 h-3 rounded-full transition-all ${
                      index === currentSlide 
                        ? 'bg-white w-8' 
                        : 'bg-white/50'
                    }`}
                  />
                ))}
              </div>
            </div>

            <Card className="bg-white/95 backdrop-blur-md shadow-2xl">
              <CardContent className="p-6 space-y-4">
                <h2 className="text-2xl font-bold text-center">Solicite uma Demo</h2>
                
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                  <div>
                    <Label htmlFor="nome">Nome completo</Label>
                    <Input 
                      id="nome" 
                      placeholder="Digite seu nome" 
                      {...form.register('nome')}
                      onFocus={() => trackFormInteraction('nome')}
                    />
                    {form.formState.errors.nome && (
                      <p className="text-sm text-destructive">{form.formState.errors.nome.message}</p>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="telefone">Telefone (apenas números)</Label>
                    <Input 
                      id="telefone" 
                      placeholder="11912345678" 
                      {...form.register('telefone')}
                      onFocus={() => trackFormInteraction('telefone')}
                    />
                    {form.formState.errors.telefone && (
                      <p className="text-sm text-destructive">{form.formState.errors.telefone.message}</p>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="empresa">Nome da empresa</Label>
                    <Input 
                      id="empresa" 
                      placeholder="Sua empresa" 
                      {...form.register('empresa')}
                      onFocus={() => trackFormInteraction('empresa')}
                    />
                    {form.formState.errors.empresa && (
                      <p className="text-sm text-destructive">{form.formState.errors.empresa.message}</p>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="quantidadePessoas">Quantas pessoas na empresa</Label>
                    <select 
                      id="quantidadePessoas" 
                      {...form.register('quantidadePessoas')}
                      onFocus={() => trackFormInteraction('quantidadePessoas')}
                      className="w-full h-10 px-3 rounded-md border border-input bg-background">
                      <option value="">Selecione</option>
                      <option value="1-10">1-10 colaboradores</option>
                      <option value="11-50">11-50 colaboradores</option>
                      <option value="51-200">51-200 colaboradores</option>
                      <option value="201+">201+ colaboradores</option>
                    </select>
                    {form.formState.errors.quantidadePessoas && (
                      <p className="text-sm text-destructive">{form.formState.errors.quantidadePessoas.message}</p>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="email">Email corporativo</Label>
                    <Input 
                      id="email" 
                      type="email" 
                      placeholder="seu@empresa.com" 
                      {...form.register('email')}
                      onFocus={() => trackFormInteraction('email')}
                    />
                    {form.formState.errors.email && (
                      <p className="text-sm text-destructive">{form.formState.errors.email.message}</p>
                    )}
                  </div>

                  <Button type="submit" className="w-full bg-primary hover:bg-primary/90" 
                    disabled={form.formState.isSubmitting || !form.formState.isValid}>
                    {form.formState.isSubmitting ? 'Enviando...' : 'Agendar Demo Gratuita'}
                  </Button>
                </form>
              </CardContent>
            </Card>

          </div>
        </div>
      </section>

      {/* SEÇÃO CLIENTES */}
      <section id="clientes" className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center mb-12">
            Empresas que <span className="text-primary">Confiam</span> em Nós
          </h2>

          <Carousel className="max-w-5xl mx-auto"
            opts={{ align: "start", loop: true }}>
            <CarouselContent>
              {[
                { name: 'Advantge', src: '/logos/advantge.jpeg' },
                { name: 'Anexa', src: '/logos/anexa.jpeg' },
                { name: 'Ares', src: '/logos/ares.jpeg' },
                { name: 'Ateris', src: '/logos/ateris.jpeg' },
                { name: 'Aura', src: '/logos/aura.png' },
                { name: 'SelectMark', src: '/logos/selectmark.jpeg' }
              ].map((cliente, idx) => (
                <CarouselItem key={idx} className="basis-1/2 md:basis-1/3 lg:basis-1/4">
                  <div className="p-4">
                    <div className="w-[106px] h-[106px] mx-auto rounded-full bg-white border border-primary/20 
                      shadow-md flex items-center justify-center hover:scale-105 transition-transform">
                      <img 
                        src={cliente.src} 
                        alt={`Logo ${cliente.name}`}
                        className="w-[106px] h-[106px] object-cover rounded-full"
                      />
                    </div>
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselPrevious className="hidden lg:flex" />
            <CarouselNext className="hidden lg:flex" />
          </Carousel>
        </div>
      </section>

      {/* SEÇÃO VÍDEO */}
      <section id="video" className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">
              Veja Como <span className="text-primary">Funciona</span>
            </h2>
            <p className="text-muted-foreground text-lg">
              Assista ao vídeo e descubra como podemos transformar seu negócio
            </p>
          </div>

          <div className="max-w-5xl mx-auto">
            <div className="aspect-video rounded-2xl overflow-hidden border border-primary/20 relative"
              style={{ boxShadow: 'var(--shadow-primary)' }}>
              <iframe 
                width="100%" 
                height="100%" 
                src="https://www.youtube.com/embed/GOZilSGdhOU?si=MWA2Q2Ga9lKqLObO" 
                title="YouTube video player" 
                frameBorder="0" 
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" 
                referrerPolicy="strict-origin-when-cross-origin" 
                allowFullScreen
                className="absolute inset-0"
              />
            </div>
          </div>
        </div>
      </section>

      {/* SEÇÃO BENEFÍCIOS */}
      <section id="beneficios" className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">
              Contrate agora e economize <span className="text-primary">98% de tempo</span> nos processos!
            </h2>
            <p className="text-lg text-muted-foreground">
              Quer saber como? Confira as funcionalidades!
            </p>
          </div>

          <div className="space-y-24">
            
            {/* Bloco 1: ADMISSÃO */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <div className="rounded-2xl overflow-hidden shadow-2xl transform transition-transform hover:scale-[1.02] border border-primary/10">
                <img 
                  src={admissaoProgressoImg} 
                  alt="Histórico de Progresso da Admissão mostrando etapas concluídas"
                  className="w-full h-auto object-cover"
                />
              </div>

              <div className="space-y-6">
                <div>
                  <h3 className="text-4xl lg:text-5xl font-bold text-primary mb-3">Admissão</h3>
                  <p className="text-lg text-muted-foreground">
                    Automatize todo o processo de contratação com inteligência artificial
                  </p>
                </div>
                
                <div className="space-y-4 mt-8">
                  {[
                    'Leitura automática de documentos em segundos',
                    'Agendamento de exame admissional feito pelo próprio candidato',
                    'Centralização de todas as etapas em um só lugar',
                    'Redução de até 70% no tempo de contratação'
                  ].map((feature, idx) => (
                    <div key={idx} className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <Check className="w-5 h-5 text-primary" />
                      </div>
                      <p className="text-foreground">{feature}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Bloco 2: CONTRATOS */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <div className="space-y-6">
                <div>
                  <h3 className="text-4xl lg:text-5xl font-bold text-primary mb-3">Contratos</h3>
                  <p className="text-lg text-muted-foreground">
                    Crie e gerencie contratos personalizados com facilidade
                  </p>
                </div>
                
                <div className="space-y-4 mt-8">
                  {[
                    'Geração automática e personalizada para cada área',
                    'Modelos prontos e editáveis dentro da plataforma',
                    'Assinatura digital integrada e segura',
                    'Histórico e controle de versões em tempo real'
                  ].map((feature, idx) => (
                    <div key={idx} className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <Check className="w-5 h-5 text-primary" />
                      </div>
                      <p className="text-foreground">{feature}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="rounded-2xl overflow-hidden shadow-2xl transform transition-transform hover:scale-[1.02] border border-primary/10">
                <img 
                  src={contratosModeloImg} 
                  alt="Interface de criação de modelo de contrato com variáveis de template"
                  className="w-full h-auto object-cover"
                />
              </div>
            </div>

            {/* Bloco 3: GESTÃO DE DOCUMENTOS */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <div className="rounded-2xl overflow-hidden shadow-2xl transform transition-transform hover:scale-[1.02] border border-primary/10">
                <img 
                  src={gestaoDocumentosImg} 
                  alt="Interface de Gestão de Documentos da Lyra RH mostrando validação automática"
                  className="w-full h-auto object-cover"
                />
              </div>

              <div className="space-y-6">
                <div>
                  <h3 className="text-4xl lg:text-5xl font-bold text-primary mb-3">Gestão de Documentos</h3>
                  <p className="text-lg text-muted-foreground">
                    Organize e valide documentos com tecnologia de IA
                  </p>
                </div>
                
                <div className="space-y-4 mt-8">
                  {[
                    'A IA da Lyra analisa e organiza todos os arquivos',
                    'Evite erros e retrabalho com leitura inteligente',
                    'Upload simplificado e armazenamento em nuvem',
                    'Visualização rápida e segura de todos os documentos'
                  ].map((feature, idx) => (
                    <div key={idx} className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <Check className="w-5 h-5 text-primary" />
                      </div>
                      <p className="text-foreground">{feature}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* SEÇÃO PLANOS */}
      <section id="planos" className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">
              Planos e <span className="text-primary">Preços</span>
            </h2>
            <p className="text-lg text-muted-foreground">
              Escolha o plano ideal para o tamanho e necessidade da sua empresa
            </p>
          </div>

          <div className="flex justify-center items-center gap-4 mb-12">
            <button 
              onClick={() => setBillingPeriod('mensal')}
              className={`px-6 py-2 rounded-full font-medium transition-colors ${
                billingPeriod === 'mensal' 
                  ? 'bg-primary text-white' 
                  : 'bg-muted text-muted-foreground hover:bg-muted/80'
              }`}
            >
              Mensal
            </button>
            <button 
              onClick={() => setBillingPeriod('anual')}
              className={`px-6 py-2 rounded-full font-medium flex items-center gap-2 transition-colors ${
                billingPeriod === 'anual' 
                  ? 'bg-primary text-white' 
                  : 'bg-muted text-muted-foreground hover:bg-muted/80'
              }`}
            >
              Anual
              <Badge className={billingPeriod === 'anual' ? 'bg-white text-primary text-xs' : 'bg-primary text-white text-xs'}>
                Mais Popular
              </Badge>
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            
            <Card className="hover:shadow-lg transition-shadow">
              <CardContent className="p-6 space-y-4">
                <div>
                  <h3 className="text-xl font-bold">Start</h3>
                  <p className="text-sm text-muted-foreground">5 contratações / R$ 43,98 por contratação</p>
                </div>
                <div>
                  <p className="text-4xl font-bold text-primary">
                    R$ {billingPeriod === 'mensal' 
                      ? plansData.essencial.monthlyPrice.toFixed(2) 
                      : plansData.essencial.annualPrice.toFixed(2)}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    /mês {billingPeriod === 'anual' && '(cobrado anualmente)'}
                  </p>
                  {billingPeriod === 'anual' && (
                    <p className="text-sm text-muted-foreground font-semibold">
                      R$ 2.110 total/ano
                    </p>
                  )}
                  {billingPeriod === 'anual' && (
                    <Badge variant="secondary" className="mt-2 bg-green-100 text-green-800">
                      Economize 20%
                    </Badge>
                  )}
                </div>
                <div className="space-y-2">
                  {(billingPeriod === 'mensal' ? plansData.essencial.monthlyFeatures : plansData.essencial.annualFeatures).map((benefit, idx) => (
                    <div key={idx} className="flex items-start gap-2 text-sm">
                      <Check className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                      <span>{benefit}</span>
                    </div>
                  ))}
                </div>
                <Button 
                  className="w-full" 
                  variant="outline"
                  onClick={() => handleSubscribe('essencial')}
                >
                  Assinar Agora
                </Button>
              </CardContent>
            </Card>

            <Card className="relative border-2 border-primary shadow-lg" 
              style={{ background: 'hsl(195 100% 50% / 0.05)' }}>
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <Badge className="bg-primary text-white">Mais Popular</Badge>
              </div>
              <CardContent className="p-6 space-y-4">
                <div>
                  <h3 className="text-xl font-bold">Growth</h3>
                  <p className="text-sm text-muted-foreground">10 contratações / R$ 37,99 por contratação</p>
                </div>
                <div>
                  <p className="text-4xl font-bold text-primary">
                    R$ {billingPeriod === 'mensal' 
                      ? plansData.profissional.monthlyPrice.toFixed(2) 
                      : plansData.profissional.annualPrice.toFixed(2)}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    /mês {billingPeriod === 'anual' && '(cobrado anualmente)'}
                  </p>
                  {billingPeriod === 'anual' && (
                    <p className="text-sm text-muted-foreground font-semibold">
                      R$ 3.650 total/ano
                    </p>
                  )}
                  {billingPeriod === 'anual' && (
                    <Badge variant="secondary" className="mt-2 bg-green-100 text-green-800">
                      Economize 20%
                    </Badge>
                  )}
                </div>
                <div className="space-y-2">
                  {(billingPeriod === 'mensal' ? plansData.profissional.monthlyFeatures : plansData.profissional.annualFeatures).map((benefit, idx) => (
                    <div key={idx} className="flex items-start gap-2 text-sm">
                      <Check className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                      <span>{benefit}</span>
                    </div>
                  ))}
                </div>
                <Button 
                  className="w-full bg-primary hover:bg-primary/90"
                  onClick={() => handleSubscribe('profissional')}
                >
                  Assinar Agora
                </Button>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-shadow">
              <CardContent className="p-6 space-y-4">
                <div>
                  <h3 className="text-xl font-bold">Scale</h3>
                  <p className="text-sm text-muted-foreground">30 contratações / R$ 28,33 por contratação</p>
                </div>
                <div>
                  <p className="text-4xl font-bold text-primary">
                    R$ {billingPeriod === 'mensal' 
                      ? plansData.corporativo.monthlyPrice.toFixed(2) 
                      : plansData.corporativo.annualPrice.toFixed(2)}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    /mês {billingPeriod === 'anual' && '(cobrado anualmente)'}
                  </p>
                  {billingPeriod === 'anual' && (
                    <p className="text-sm text-muted-foreground font-semibold">
                      R$ 8.155 total/ano
                    </p>
                  )}
                  {billingPeriod === 'anual' && (
                    <Badge variant="secondary" className="mt-2 bg-green-100 text-green-800">
                      Economize 20%
                    </Badge>
                  )}
                </div>
                <div className="space-y-2">
                  {(billingPeriod === 'mensal' ? plansData.corporativo.monthlyFeatures : plansData.corporativo.annualFeatures).map((benefit, idx) => (
                    <div key={idx} className="flex items-start gap-2 text-sm">
                      <Check className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                      <span>{benefit}</span>
                    </div>
                  ))}
                </div>
                <Button 
                  className="w-full" 
                  variant="outline"
                  onClick={() => handleSubscribe('corporativo')}
                >
                  Assinar Agora
                </Button>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-shadow">
              <CardContent className="p-6 space-y-4">
                <div>
                  <h3 className="text-xl font-bold">Personalizado</h3>
                  <p className="text-sm text-muted-foreground">Ilimitadas contratações</p>
                </div>
                <div>
                  <p className="text-3xl font-bold text-primary">Sob consulta</p>
                </div>
                <div className="space-y-2">
                  {[
                    'Flexibilidade total (plano moldado conforme demandas)',
                    'Soluções sob medida (integrações e suporte dedicado)',
                    'Investimento inteligente (pague apenas pelo que usar)'
                  ].map((benefit, idx) => (
                    <div key={idx} className="flex items-start gap-2 text-sm">
                      <Check className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                      <span>{benefit}</span>
                    </div>
                  ))}
                </div>
                <Button className="w-full" variant="outline">Entrar em Contato</Button>
              </CardContent>
            </Card>

          </div>

        </div>

        <div className="p-12 text-center text-white w-full"
          style={{ background: 'var(--gradient-hero)' }}>
          <h3 className="text-3xl font-bold mb-4">
            Ficou curioso sobre como podemos ajudar?
          </h3>
          <p className="text-lg mb-6 opacity-90">
            Agende uma demonstração gratuita e veja na prática como nossa IA pode transformar seu RH
          </p>
          <Button size="lg" variant="secondary" 
            onClick={() => navigate('/demo')}
            className="bg-white text-primary hover:bg-white/90">
            Agendar Demo Gratuita
          </Button>
        </div>
      </section>

      {/* SEÇÃO DEPOIMENTOS */}
      <section id="depoimentos" className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12 max-w-3xl mx-auto">
            <h2 className="text-4xl font-bold mb-6">
              Por que escolher a plataforma de RH da <span className="text-primary">Lyra</span>?
            </h2>
            <p className="text-lg text-muted-foreground mb-8">
              A Lyra foi criada para simplificar e acelerar a rotina do seu RH com tecnologia de ponta em 
              Inteligência Artificial. Com ela, você automatiza tarefas repetitivas, reduz erros e ganha 
              tempo para o que realmente importa: as pessoas.
            </p>
            
            <div className="text-center max-w-2xl mx-auto space-y-3">
              {[
                'Leitura automática de documentos',
                'Criação de contratos personalizados',
                'Agendamento automático de exames admissionais',
                'Painel inteligente com indicadores de desempenho',
                'Integração com ferramentas e CRMs do mercado'
              ].map((feature, idx) => (
                <div key={idx} className="flex items-start gap-3 justify-center">
                  <div className="w-2 h-2 rounded-full bg-primary mt-2 flex-shrink-0" />
                  <p className="text-foreground">{feature}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            
            <Card>
              <CardContent className="p-6 space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center"
                    style={{ background: 'var(--gradient-hero)' }}>
                    <Star className="w-6 h-6 text-white fill-white" />
                  </div>
                  <div>
                    <p className="font-bold">Renato Grillo</p>
                    <p className="text-sm text-muted-foreground">CEO</p>
                    <p className="text-sm text-primary">SelectMark</p>
                  </div>
                </div>
                <p className="italic text-muted-foreground">
                  "A Lyra RH revolucionou nossa gestão de pessoas. Reduzimos o tempo de contratação em 70% 
                  e eliminamos completamente os erros em documentação. A plataforma é intuitiva e a IA 
                  realmente entrega o que promete."
                </p>
                <div className="flex gap-1">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star key={star} className="w-5 h-5 text-primary fill-primary" />
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6 space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center"
                    style={{ background: 'var(--gradient-hero)' }}>
                    <Star className="w-6 h-6 text-white fill-white" />
                  </div>
                  <div>
                    <p className="font-bold">Denise dos Santos Souza</p>
                    <p className="text-sm text-muted-foreground">Coordenadora de RH</p>
                    <p className="text-sm text-primary">Ares</p>
                  </div>
                </div>
                <p className="italic text-muted-foreground">
                  "Antes da Lyra, passávamos dias validando documentos manualmente. Agora, em minutos temos 
                  tudo processado e organizado. Nossa equipe pode focar em estratégia ao invés de tarefas 
                  operacionais."
                </p>
                <div className="flex gap-1">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star key={star} className="w-5 h-5 text-primary fill-primary" />
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6 space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center"
                    style={{ background: 'var(--gradient-hero)' }}>
                    <Star className="w-6 h-6 text-white fill-white" />
                  </div>
                  <div>
                    <p className="font-bold">Bhenda Chatterley dos Santos Gomes</p>
                    <p className="text-sm text-muted-foreground">Assistente de RH</p>
                    <p className="text-sm text-primary">Ares</p>
                  </div>
                </div>
                <p className="italic text-muted-foreground">
                  "A integração com WhatsApp mudou completamente nossa comunicação com candidatos. O 
                  agendamento automático de exames e a centralização de documentos tornaram meu trabalho 
                  muito mais eficiente."
                </p>
                <div className="flex gap-1">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star key={star} className="w-5 h-5 text-primary fill-primary" />
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6 space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center"
                    style={{ background: 'var(--gradient-hero)' }}>
                    <Star className="w-6 h-6 text-white fill-white" />
                  </div>
                  <div>
                    <p className="font-bold">Julio Cezar Junqueira Martino</p>
                    <p className="text-sm text-muted-foreground">Gerente</p>
                    <p className="text-sm text-primary">Anexa</p>
                  </div>
                </div>
                <p className="italic text-muted-foreground">
                  "Implementamos a Lyra há 6 meses e o ROI foi imediato. A geração automática de contratos e 
                  a assinatura digital reduziram nosso ciclo de admissão pela metade. Investimento que se 
                  paga sozinho."
                </p>
                <div className="flex gap-1">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star key={star} className="w-5 h-5 text-primary fill-primary" />
                  ))}
                </div>
              </CardContent>
            </Card>

          </div>
        </div>
      </section>

      {/* SEÇÃO CONTATO */}
      <section id="contato" className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">
              Entre em <span className="text-primary">Contato</span>
            </h2>
            <p className="text-lg text-muted-foreground">
              Nossa equipe está pronta para ajudar você a transformar seu RH
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            
            <Card className="hover:shadow-lg hover:scale-105 transition-all cursor-pointer"
              onClick={() => window.open('https://wa.me/5511976121346', '_blank')}>
              <CardContent className="p-6 text-center space-y-4">
                <div className="w-16 h-16 mx-auto rounded-full flex items-center justify-center"
                  style={{ background: 'var(--gradient-hero)' }}>
                  <MessageCircle className="w-8 h-8 text-white" />
                </div>
                <div>
                  <p className="font-bold text-lg mb-1">WhatsApp</p>
                  <p className="text-primary font-medium">(11) 97612-1346</p>
                  <p className="text-sm text-muted-foreground">Clique para conversar conosco</p>
                </div>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg hover:scale-105 transition-all">
              <CardContent className="p-6 text-center space-y-4">
                <div className="w-16 h-16 mx-auto rounded-full flex items-center justify-center"
                  style={{ background: 'var(--gradient-hero)' }}>
                  <Clock className="w-8 h-8 text-white" />
                </div>
                <div>
                  <p className="font-bold text-lg mb-1">Horário de Atendimento</p>
                  <p className="font-medium">Segunda a Sexta</p>
                  <p className="text-muted-foreground">9h às 18h</p>
                </div>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg hover:scale-105 transition-all cursor-pointer"
              onClick={() => window.location.href = 'mailto:contato@lyrarh.com.br'}>
              <CardContent className="p-6 text-center space-y-4">
                <div className="w-16 h-16 mx-auto rounded-full flex items-center justify-center"
                  style={{ background: 'var(--gradient-hero)' }}>
                  <Mail className="w-8 h-8 text-white" />
                </div>
                <div>
                  <p className="font-bold text-lg mb-1">E-mail</p>
                  <p className="text-primary font-medium">contato@lyrarh.com.br</p>
                  <p className="text-sm text-muted-foreground">Respondemos em até 24 horas</p>
                </div>
              </CardContent>
            </Card>

          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="py-12 text-center" 
        style={{ backgroundColor: 'hsl(220 13% 13%)', color: 'hsl(0 0% 100%)' }}>
        <div className="container mx-auto px-4 space-y-6">
          <div className="flex items-center justify-center gap-3">
            <div className="w-12 h-12 rounded-lg flex items-center justify-center text-white font-bold text-2xl"
              style={{ background: 'var(--gradient-hero)' }}>
              L
            </div>
            <span className="text-2xl font-bold">Lyra RH</span>
          </div>

          <p className="text-white/80 max-w-2xl mx-auto">
            Transformando o futuro dos Recursos Humanos com Inteligência Artificial
          </p>

          <div className="border-t border-white/20 max-w-4xl mx-auto" />

          <p className="text-white/60 text-sm">
            © 2025 Lyra RH. Todos os direitos reservados.
          </p>
        </div>
      </footer>

    </div>
  );
}
